import React, { useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { getTracks, fetchTrackArt, uploadAlbumArt } from '../api';
import NavControls from '../components/NavControls';

export default function ArtFetcher() {
  const [activeTab, setActiveTab] = useState('bulk');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkLog, setBulkLog] = useState([]);
  const [bulkStats, setBulkStats] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [fetchingArt, setFetchingArt] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const logRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Search tracks for single mode
  const { data: searchData } = useQuery(
    ['art-search', search],
    () => getTracks({ search, page_size: 20 }).then(r => r.data),
    { enabled: search.length > 2 }
  );
  const searchResults = searchData?.results || [];

  const startBulk = () => {
    setBulkRunning(true);
    setBulkLog([]);
    setBulkStats(null);

    const es = new EventSource('http://localhost:8000/api/tracks/stream-art/');

    es.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.type === 'start') {
        setBulkLog([`Starting — ${data.total} albums missing art`]);
      } else if (data.type === 'progress') {
        setBulkLog(prev => {
          const next = [...prev, `[${data.current}/${data.total}] ${data.message}`];
          // Auto scroll
          setTimeout(() => {
            if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
          }, 50);
          return next;
        });
      } else if (data.type === 'done') {
        setBulkStats(data);
        setBulkRunning(false);
        es.close();
      }
    };

    es.onerror = () => {
      setBulkLog(prev => [...prev, 'Connection error — check backend logs']);
      setBulkRunning(false);
      es.close();
    };
  };

  const handleFetchSingle = async () => {
    if (!selectedTrack) return;
    setFetchingArt(true);
    setFetchResult(null);
    try {
      const res = await fetchTrackArt(selectedTrack.id);
      setFetchResult(res.data);
      if (res.data.success) {
        setSelectedTrack(prev => ({ ...prev, cover_art_url: `${res.data.cover_art_url}?t=${Date.now()}` }));
      }
    } catch (e) {
      setFetchResult({ success: false, message: 'Request failed' });
    }
    setFetchingArt(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedTrack) return;
    // Reset input so the same file can be re-selected after an error
    e.target.value = '';
    setUploading(true);
    setFetchResult(null);
    try {
        const res = await uploadAlbumArt(selectedTrack.album, file);
        if (res.data.error) {
          setFetchResult({ success: false, message: res.data.error });
        } else {
          setFetchResult({ success: true, message: 'Art uploaded successfully!' });
          setSelectedTrack(prev => ({ ...prev, cover_art_url: `${res.data.cover_art_url}?t=${Date.now()}` }));
        }
    } catch (e) {
        const msg = e.response?.data?.detail || e.response?.data?.error || e.message || 'Upload failed';
        setFetchResult({ success: false, message: msg });
    }
    setUploading(false);
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>Album Art</h1>
          <NavControls />
        </div>
        <p>Fetch cover art from MusicBrainz / Cover Art Archive</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['bulk', 'single'].map(tab => (
          <button
            key={tab}
            className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'bulk' ? '🖼 Bulk Fetch Missing Art' : '🔍 Single Album'}
          </button>
        ))}
      </div>

      {activeTab === 'bulk' && (
        <div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
            Fetches art for all albums with no existing art file. Will not overwrite anything you already have.
          </p>

          <button
            className="btn btn-primary"
            onClick={startBulk}
            disabled={bulkRunning}
            style={{ marginBottom: 16 }}
          >
            {bulkRunning ? '⏳ Running…' : '▶ Start Bulk Fetch'}
          </button>

          {bulkStats && (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: 16, marginBottom: 16,
              display: 'flex', gap: 32
            }}>
              <div><div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{bulkStats.success}</div><div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Fetched</div></div>
              <div><div style={{ fontSize: 24, fontWeight: 700 }}>{bulkStats.failed}</div><div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Not Found</div></div>
              <div><div style={{ fontSize: 24, fontWeight: 700 }}>{bulkStats.total}</div><div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Total</div></div>
            </div>
          )}

          {bulkLog.length > 0 && (
            <div
              ref={logRef}
              style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 12, height: 400, overflowY: 'auto',
                fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)',
              }}
            >
              {bulkLog.map((line, i) => (
                <div key={i} style={{ color: line.includes('saved') ? 'var(--accent)' : 'var(--text-muted)' }}>
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'single' && (
        <div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
            Search for a track, then fetch new art. This will overwrite existing art for that album.
          </p>

          <input
            className="search-input"
            placeholder="Search by song, artist, or album…"
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedTrack(null); setFetchResult(null); }}
            style={{ marginBottom: 16, maxWidth: 400 }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />

          {searchResults.length > 0 && !selectedTrack && (
            <table className="track-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Album</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(track => (
                  <tr key={track.id} onClick={() => { setSelectedTrack(track); console.log(track); setFetchResult(null); }} style={{ cursor: 'pointer' }}>
                    <td><strong>{track.title}</strong></td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.artist_name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{track.album_title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selectedTrack && (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: 24, display: 'flex', gap: 24, alignItems: 'flex-start'
            }}>
              {/* Current art */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>Current Art</div>
                {selectedTrack.cover_art_url
                  ? <img key={selectedTrack.cover_art_url} src={selectedTrack.cover_art_url} alt="" style={{ width: 160, height: 160, borderRadius: 8, objectFit: 'cover' }} />
                  : <div style={{ width: 160, height: 160, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🎵</div>
                }
              </div>

              {/* Info + action */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedTrack.album_title}</div>
                <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{selectedTrack.artist_name}</div>

                <button
                  className="btn btn-primary"
                  onClick={handleFetchSingle}
                  disabled={fetchingArt}
                  style={{ marginBottom: 12 }}
                >
                  {fetchingArt ? '⏳ Fetching…' : '🔍 Fetch Art from MusicBrainz'}
                </button>

                <button
                className="btn btn-secondary"
                onClick={() => fileInputRef.current.click()}
                disabled={uploading}
                style={{ marginBottom: 12, marginLeft: 8 }}
                >
                {uploading ? '⏳ Uploading…' : '📁 Upload Art'}
                </button>
                <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleUpload}
                />
                
                <button
                  className="btn btn-secondary"
                  onClick={() => { setSelectedTrack(null); setSearch(''); }}
                  style={{ marginLeft: 8 }}
                >
                  ← Back to search
                </button>

                {fetchResult && (
                  <div style={{
                    marginTop: 12, padding: '8px 12px', borderRadius: 6,
                    background: fetchResult.success ? 'rgba(105, 190, 40, 0.15)' : 'rgba(255,50,50,0.15)',
                    color: fetchResult.success ? 'var(--accent)' : '#ff6b6b',
                    fontSize: 13
                  }}>
                    {fetchResult.success ? '✓ ' : '✗ '}{fetchResult.message}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}